/**
 * FiveYearRuleOptimizer - Ensures regulatory compliance by guaranteeing each bond series
 * has at least $10,000 principal payment within its first 5 payment periods.
 *
 * Regulatory requirement: Municipal bonds must have principal payments within 5 calendar
 * years of their first principal payment date for tax-exempt status compliance.
 */
import { PrincipalBasedRecalculationEngine } from '../../engines/cashflow-engine/principal-recalculator';
import { ProcessingFinalizer } from '../../engines/cashflow-engine/processing-finalizer';
import { CandidateNaming } from '../../shared/candidate-naming';
import type {
  FiveYearRuleOptimizationConfig,
  FiveYearRuleViolation,
  OptimizationStrategy,
  PeriodInfo,
} from '../../types';

// Five-year rule utilities are now in this file

import { PRINCIPAL_AMOUNTS } from '~/config/financial-constants';
import { DateCalculationEngine } from '~/engine/processing/shared/date-calculations';
import { logger } from '~/services/logging';
import type { Candidate, Configuration } from '~/types';
import { addYears } from '~/utils/date-utils';

export class FiveYearRuleOptimizer implements OptimizationStrategy {
  private config: FiveYearRuleOptimizationConfig;

  constructor(config: FiveYearRuleOptimizationConfig = {}) {
    this.config = {
      minimumPrincipal: PRINCIPAL_AMOUNTS.MINIMUM_PRINCIPAL,
      ...config,
    };
  }

  optimize(candidate: Candidate, configuration: Configuration): Candidate {
    // Always log at start to verify we're being called
    logger.L3.info(`📋 FiveYearRuleOptimizer: Starting (logging=${this.config.enableLogging})`);

    if (this.config.enableLogging) {
      logger.L3.info('📋 FiveYearRuleOptimizer: Starting five-year rule compliance optimization');
    }

    const violations = checkFiveYearRuleCompliance(candidate, configuration);

    if (violations.length === 0) {
      if (this.config.enableLogging) {
        logger.L3.info('✅ All bonds comply with five-year rule');
      }
      return candidate;
    }

    if (this.config.enableLogging) {
      logger.L3.warn(`⚠️ Found ${violations.length} five-year rule violations`);
      violations.forEach((v) => {
        logger.domain.compliance(
          `  - ${v.bondName}: No principal payments within 5 years of ${v.firstPrincipalDate}`
        );
      });
    }

    // Create optimized candidate to fix violations
    const optimizedCandidate = this.createOptimizedCandidate(candidate);
    let totalAdjustments = 0;

    for (const violation of violations) {
      const adjustments = this.fixFiveYearRuleViolation(
        optimizedCandidate,
        violation,
        configuration
      );
      totalAdjustments += adjustments;
    }

    if (totalAdjustments > 0) {
      // Add optimization history entry
      if (!optimizedCandidate.optimizationHistory) {
        optimizedCandidate.optimizationHistory = [];
      }

      // Build description of what was fixed
      const fixDescriptions = violations.map(
        (v) =>
          `${v.bondName}: moved principal to ensure compliance within 5 years of ${v.firstPrincipalDate}`
      );

      optimizedCandidate.optimizationHistory.push({
        type: 'five-year-fix',
        description: `Fixed ${violations.length} five-year rule violation${violations.length > 1 ? 's' : ''}`,
        timestamp: new Date().toISOString(),
        details: {
          bondSeriesAffected: violations.map((v) => v.bondId),
          violationsFixed: violations.length,
          adjustmentDetails: fixDescriptions,
        },
      });

      // Finalize the optimized candidate after principal transfers
      // This handles cash flows, tax year plan, results evaluation, and five-year rule validation
      const finalizationResult = ProcessingFinalizer.finalizeAfterOptimization(
        optimizedCandidate,
        configuration
      );

      if (finalizationResult.success) {
        if (this.config.enableLogging) {
          logger.L3.info(
            `✅ Fixed ${violations.length} violations with ${totalAdjustments} adjustments`
          );
        }
        return optimizedCandidate;
      }
    }

    if (this.config.enableLogging) {
      logger.L3.error('❌ Could not fix five-year rule violations');
    }
    return candidate;
  }

  /**
   * Fixes a single five-year rule violation by transferring principal
   */
  private fixFiveYearRuleViolation(
    candidate: Candidate,
    violation: FiveYearRuleViolation,
    configuration: Configuration
  ): number {
    if (this.config.enableLogging) {
      logger.L3.info(`🔧 Fixing violation for ${violation.bondName}`);
    }

    // Step 1: Find where to take principal from (prefer final year for interest savings)
    const sourceInfo = this.findBestPeriodToTakeFrom(candidate, violation.bondId, configuration);
    if (!sourceInfo) {
      if (this.config.enableLogging) {
        logger.L3.error(
          `  ❌ Could not find period to take principal from for ${violation.bondName}`
        );
      }
      return 0;
    }

    // Step 2: Find where to add it among the first 5 periods
    const targetInfo = this.findBestPeriodForPrincipalAddition(candidate, violation);
    if (!targetInfo) {
      if (this.config.enableLogging) {
        logger.L3.error(`  ❌ Could not find suitable target period for ${violation.bondName}`);
      }
      return 0;
    }

    // Step 3: Apply the transfer
    const transferSuccess = this.applyPrincipalTransfer(
      candidate,
      violation.bondId,
      sourceInfo,
      targetInfo,
      configuration
    );

    if (transferSuccess) {
      if (this.config.enableLogging) {
        logger.L3.info(`  ✅ Successfully transferred principal for ${violation.bondName}`);
      }
      return 1;
    }

    if (this.config.enableLogging) {
      logger.L3.info(`  ❌ Could not apply transfer for ${violation.bondName}`);
    }
    return 0;
  }

  /**
   * Finds the best period to take principal from (prefers later periods)
   * For fixed target bonds: Only redistributes within the same bond
   * For unlimited bonds: Can take from any viable period
   */
  private findBestPeriodToTakeFrom(
    candidate: Candidate,
    bondId: string,
    configuration: Configuration
  ): PeriodInfo | null {
    const targetBond = configuration.bondSeries.find((bond) => bond.id === bondId);
    if (!targetBond) {
      throw new Error(`Bond not found: ${bondId} in candidate ${candidate.id}`);
    }

    // Find all cash flows for this specific bond with principal > 0, sorted by date (latest first)
    const bondsWithPrincipal = candidate.cashFlows
      .filter((cf) => {
        const bondPayment = cf.bondPayments.find((bp) => bp.bondSeriesId === bondId);
        // Only look at cash flows for this specific bond that have principal payments
        return bondPayment && bondPayment.principal > 0 && !cf.isInterestOnly;
      })
      .sort((a, b) => b.paymentDate.localeCompare(a.paymentDate)); // Latest first

    if (bondsWithPrincipal.length === 0) {
      if (this.config.enableLogging) {
        logger.L3.info(`  ❌ No periods with principal found for ${targetBond?.name}`);
      }
      return null;
    }

    const minimumTransfer = this.config.minimumPrincipal || PRINCIPAL_AMOUNTS.MINIMUM_PRINCIPAL;

    // First preference: periods with at least the minimum amount
    const periodsWithEnough = bondsWithPrincipal.filter((cf) => {
      const bondPayment = cf.bondPayments.find((bp) => bp.bondSeriesId === bondId)!;
      return bondPayment.principal >= minimumTransfer;
    });

    if (periodsWithEnough.length > 0) {
      const finalPeriod = periodsWithEnough[0]; // Already sorted latest first
      const bondPayment = finalPeriod.bondPayments.find((bp) => bp.bondSeriesId === bondId)!;

      return {
        paymentDate: finalPeriod.paymentDate,
        principalAmount: bondPayment.principal,
      };
    }

    // Fallback: take what we can from the latest period with any principal
    const latestPeriod = bondsWithPrincipal[0];
    const bondPayment = latestPeriod.bondPayments.find((bp) => bp.bondSeriesId === bondId)!;

    return {
      paymentDate: latestPeriod.paymentDate,
      principalAmount: bondPayment.principal,
    };
  }

  /**
   * Finds the best period within the 5-year window to add principal
   * For fixed target bonds: Redistributes within the same bond
   * For unlimited bonds: Can add principal to any viable period with capacity
   */
  private findBestPeriodForPrincipalAddition(
    candidate: Candidate,
    violation: FiveYearRuleViolation
  ): PeriodInfo | null {
    const periodsWithCapacity: PeriodInfo[] = [];
    let firstPrincipalPeriod: PeriodInfo | null = null;

    for (const period of violation.periodsInWindow) {
      // Skip interest-only periods
      if (period.isInterestOnly) {
        continue;
      }

      // Track the first principal period as fallback
      if (!firstPrincipalPeriod) {
        firstPrincipalPeriod = {
          paymentDate: period.paymentDate,
          principalAmount: period.principal,
        };
      }

      const cashFlow = candidate.cashFlows.find((cf) => cf.paymentDate === period.paymentDate);
      if (!cashFlow) {
        continue;
      }

      // Check if this period has capacity for additional principal
      const availableCapacity = cashFlow.availableForBonds;
      const minimumTransfer = this.config.minimumPrincipal || PRINCIPAL_AMOUNTS.MINIMUM_PRINCIPAL;

      if (availableCapacity >= minimumTransfer) {
        periodsWithCapacity.push({
          paymentDate: period.paymentDate,
          principalAmount: period.principal,
          hasCapacity: true,
          availableCapacity,
        });
      }
    }

    // If any periods have capacity, use the earliest one
    if (periodsWithCapacity.length > 0) {
      // Already in chronological order from violation.periodsInWindow
      return periodsWithCapacity[0];
    }

    // If no periods have capacity, default to the first principal period
    return firstPrincipalPeriod;
  }

  /**
   * Applies a principal transfer between source and target periods
   */
  private applyPrincipalTransfer(
    candidate: Candidate,
    bondId: string,
    sourceInfo: PeriodInfo,
    targetInfo: PeriodInfo,
    configuration: Configuration
  ): boolean {
    // Find source cash flow and bond payment
    const sourceCashFlow = candidate.cashFlows.find(
      (cf) => cf.paymentDate === sourceInfo.paymentDate
    );
    const sourceBondPayment = sourceCashFlow?.bondPayments.find((bp) => bp.bondSeriesId === bondId);

    // Find target cash flow and bond payment
    const targetCashFlow = candidate.cashFlows.find(
      (cf) => cf.paymentDate === targetInfo.paymentDate
    );
    const targetBondPayment = targetCashFlow?.bondPayments.find((bp) => bp.bondSeriesId === bondId);

    if (!sourceCashFlow || !sourceBondPayment || !targetCashFlow || !targetBondPayment) {
      return false;
    }

    // Calculate transfer amount (up to minimum required or whatever is available)
    const minimumTransfer = this.config.minimumPrincipal || PRINCIPAL_AMOUNTS.MINIMUM_PRINCIPAL;
    const transferAmount = Math.min(minimumTransfer, sourceBondPayment.principal);

    if (transferAmount <= 0) {
      return false;
    }

    // Apply the transfer
    if (this.config.enableLogging) {
      logger.L3.info(`  💰 Transferring $${transferAmount.toLocaleString()} principal`);
      logger.domain.compliance(
        `     From: ${sourceInfo.paymentDate} (was $${sourceBondPayment.principal.toLocaleString()})`
      );
      logger.L3.info(
        `     To: ${targetInfo.paymentDate} (was $${targetBondPayment.principal.toLocaleString()})`
      );
    }

    // Remove from source
    sourceBondPayment.principal -= transferAmount;
    targetBondPayment.principal += transferAmount;

    if (this.config.enableLogging) {
      logger.domain.compliance(
        `     After transfer - From: $${sourceBondPayment.principal.toLocaleString()}, To: $${targetBondPayment.principal.toLocaleString()}`
      );
      logger.L3.info(`  🔄 Triggering principal-based recalculation...`);
    }

    // Perform comprehensive recalculation based on new principal distribution
    PrincipalBasedRecalculationEngine.recalculateFromPrincipal(candidate, configuration.bondSeries);

    return true;
  }

  /**
   * Creates a deep copy of candidate for optimization
   */
  private createOptimizedCandidate(candidate: Candidate): Candidate {
    // Count actual optimizations (excluding initial-shape and optimization-skipped)
    const appliedOptCount =
      candidate.optimizationHistory?.filter(
        (h) => h.type !== 'initial-shape' && h.type !== 'optimization-skipped'
      ).length || 0;
    const newOptCount = appliedOptCount + 1;

    // Use the centralized naming system: {shapeId}.{count}
    const newName = CandidateNaming.createOptimizationName(candidate.name, newOptCount);

    // Generate new unique ID
    const shapeId = CandidateNaming.getShapeId(candidate.name);

    return {
      ...candidate,
      id: `${shapeId}.${newOptCount}-${crypto.randomUUID().slice(0, 8)}`,
      name: newName,
      cashFlows: candidate.cashFlows.map((cf) => ({
        ...cf,
        bondPayments: cf.bondPayments.map((bp) => ({ ...bp })),
      })),
      // Note: bondSeries removed from candidate - this is now in configuration
      // This field should not be copied when cloning candidates
      // Copy and prepare to extend optimization history
      optimizationHistory: [...(candidate.optimizationHistory || [])],
      // Reset results to clear failure messages from the original candidate
      // The finalization process will recalculate these
      results: {
        isViable: true,
        failureMessages: [],
        totalRevenue: 0,
        totalInterest: 0,
        totalPrincipal: 0,
        revenueUtilization: 0,
        interestRatio: 0,
        efficiency: 0,
        bondSeriesResults: [],
      },
    };
  }
}

// ============================================================================
// Five-Year Rule Utility Functions (consolidated from five-year-rule-utilities.ts)
// ============================================================================

/**
 * Checks five-year rule compliance for all bonds in a candidate
 *
 * Five-year rule requirement: Each municipal bond must have at least one
 * principal payment within 5 calendar years of its first principal payment date.
 *
 * @param candidate - The candidate to check
 * @param configuration - Configuration data (required for bond series access)
 * @returns Array of violations (empty if all bonds are compliant)
 */
export function checkFiveYearRuleCompliance(
  candidate: Candidate,
  configuration: Configuration
): FiveYearRuleViolation[] {
  const violations: FiveYearRuleViolation[] = [];

  for (const bond of configuration.bondSeries) {
    // Find bond ISSUE date (first cash flow with any payment for this bond)
    // The five-year rule applies from when the bond is ISSUED, not when principal starts
    const bondIssueCashFlow = candidate.cashFlows
      .filter((cf) => {
        const bondPayment = cf.bondPayments.find((bp) => bp.bondSeriesId === bond.id);
        return bondPayment && (bondPayment.principal > 0 || bondPayment.totalInterest > 0);
      })
      .sort((a, b) => a.paymentDate.localeCompare(b.paymentDate))[0]; // First by date

    if (!bondIssueCashFlow) {
      violations.push({
        bondId: bond.id,
        bondName: bond.name,
        firstPrincipalDate: 'No cash flows found',
        fiveYearDeadline: 'N/A',
        periodsInWindow: [],
        totalPrincipalInWindow: 0,
      });
      continue;
    }

    const bondIssueDate = bondIssueCashFlow.paymentDate;

    // Calculate 5-year compliance window from BOND ISSUE DATE
    const fiveYearDeadline = addYears(bondIssueDate, 5);

    // Find all cash flows for this bond within the 5-year window from issue date
    const cashFlowsInWindow = candidate.cashFlows
      .filter((cf) => {
        const bondPayment = cf.bondPayments.find((bp) => bp.bondSeriesId === bond.id);
        if (!bondPayment || !cf.isActive) return false;

        return DateCalculationEngine.isWithinYearWindow(cf.paymentDate, bondIssueDate, 5);
      })
      .sort((a, b) => a.paymentDate.localeCompare(b.paymentDate));

    // Extract payment information for the window
    const periodsInWindow = cashFlowsInWindow.map((cf) => {
      const bondPayment = cf.bondPayments.find((bp) => bp.bondSeriesId === bond.id)!;
      return {
        paymentDate: cf.paymentDate,
        principal: bondPayment.principal,
        isInterestOnly: cf.isInterestOnly,
      };
    });

    // Check for compliance (any principal payment within the window)
    const totalPrincipalInWindow = periodsInWindow.reduce(
      (total, period) => total + period.principal,
      0
    );

    if (totalPrincipalInWindow === 0) {
      violations.push({
        bondId: bond.id,
        bondName: bond.name,
        firstPrincipalDate: bondIssueDate, // Issue date, not first principal date
        fiveYearDeadline: fiveYearDeadline, // Already a string in YYYY-MM-DD format
        periodsInWindow,
        totalPrincipalInWindow: totalPrincipalInWindow,
      });
    }
  }

  return violations;
}

/**
 * Validates five-year rule compliance and marks candidate as non-viable if violations exist.
 * This is the validation version used during candidate evaluation.
 *
 * @param candidate The candidate to validate (modified in place)
 * @param configuration Configuration containing bond series
 */
export function validateFiveYearRuleCompliance(
  candidate: Candidate,
  configuration: Configuration
): void {
  // Only check if the candidate is currently viable
  if (!candidate.results.isViable) {
    return;
  }

  const violations = checkFiveYearRuleCompliance(candidate, configuration);

  if (violations.length > 0) {
    // Mark candidate as non-viable
    candidate.results.isViable = false;

    // Add failure messages for each violation
    for (const violation of violations) {
      candidate.results.failureMessages.push(
        `Five-year rule violation: ${violation.bondName} has no principal payments within 5 years of first principal payment date (${violation.firstPrincipalDate})`
      );
    }
  }
}

/**
 * Gets a human-readable summary of five-year rule compliance
 */
export function getFiveYearRuleComplianceSummary(
  candidate: Candidate,
  configuration: Configuration
): {
  isCompliant: boolean;
  totalBonds: number;
  compliantBonds: number;
  violations: FiveYearRuleViolation[];
  summary: string;
} {
  const violations = checkFiveYearRuleCompliance(candidate, configuration);
  const totalBonds = configuration.bondSeries.length;
  const compliantBonds = totalBonds - violations.length;
  const isCompliant = violations.length === 0;

  let summary: string;
  if (isCompliant) {
    summary = `All ${totalBonds} bond${totalBonds !== 1 ? 's' : ''} comply with the five-year rule`;
  } else {
    summary = `${violations.length} of ${totalBonds} bond${totalBonds !== 1 ? 's' : ''} violate the five-year rule`;
  }

  return {
    isCompliant,
    totalBonds,
    compliantBonds,
    violations,
    summary,
  };
}
